/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-11
 */

import { useState, useEffect } from 'react';

export type ThemeMode = 'light' | 'dark';

/**
 * Custom hook for managing theme mode state with persistence and system preference detection.
 * @returns Object containing current theme mode and toggle function
 */
export const useThemeMode = () => {
  // Get system preference for dark mode
  const getSystemPreference = (): ThemeMode => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  };

  // Get stored preference or fall back to system preference
  const getStoredPreference = (): ThemeMode => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('tools-dance-theme-mode');
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    }
    return getSystemPreference();
  };

  const [mode, setMode] = useState<ThemeMode>(getStoredPreference);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = (e: MediaQueryListEvent) => {
        // Only update if no user preference is stored
        const stored = localStorage.getItem('tools-dance-theme-mode');
        if (!stored) {
          setMode(e.matches ? 'dark' : 'light');
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
    return undefined;
  }, []);

  // Persist theme mode to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('tools-dance-theme-mode', mode);
    }
  }, [mode]);

  /**
   * Toggle between light and dark mode
   */
  const toggleMode = () => {
    setMode(prevMode => (prevMode === 'light' ? 'dark' : 'light'));
  };

  /**
   * Set specific theme mode
   * @param newMode - The theme mode to set
   */
  const setThemeMode = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  return {
    mode,
    toggleMode,
    setThemeMode,
    isLight: mode === 'light',
    isDark: mode === 'dark',
  };
};

export default useThemeMode;
